import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PlusCircle, Trash2, FileText, Mail } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useAdminPreview } from '@/lib/admin-preview-context';

interface LegalDocument {
  id: string;
  name: string;
  content: string;
  updated_at: string;
  role_scope: string | null;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  role_scope: string | null;
}

export default function AdminSettings() {
  const { isPreviewMode } = useAdminPreview();
  const { toast } = useToast();
  const [contactEmail, setContactEmail] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(true);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [editingDoc, setEditingDoc] = useState<LegalDocument | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [isSavingDoc, setIsSavingDoc] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [isCreatingDoc, setIsCreatingDoc] = useState(false);

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoadingFaqs, setIsLoadingFaqs] = useState(true);

  const [newFaq, setNewFaq] = useState<{ question: string; answer: string }>({
    question: '',
    answer: '',
  });
  const [editingFaq, setEditingFaq] = useState<string | null>(null);

  const roleScope = isPreviewMode ? 'hoofdhartuser' : null;
  const emailKey = isPreviewMode ? 'hnh_contact_email' : 'contact_email';

  // Fetch contact email
  useEffect(() => {
    const fetchContactEmail = async () => {
      setIsLoadingEmail(true);
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', emailKey)
          .single();

        if (error) {
          console.error('Error fetching contact email:', error);
          setContactEmail('');
          return;
        }

        setContactEmail(data.value);
      } catch (error) {
        console.error('Error fetching contact email:', error);
      } finally {
        setIsLoadingEmail(false);
      }
    };

    fetchContactEmail();
  }, [emailKey]);

  // Fetch documents filtered by role_scope
  useEffect(() => {
    const fetchDocuments = async () => {
      setIsLoadingDocs(true);
      try {
        let query = supabase
          .from('legal_documents')
          .select('*')
          .order('name');

        if (roleScope) {
          query = query.eq('role_scope', roleScope);
        } else {
          query = query.is('role_scope', null);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching documents:', error);
          toast({
            title: 'Fout',
            description: 'Kon documenten niet laden.',
            variant: 'destructive',
          });
          return;
        }

        setDocuments(data ?? []);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoadingDocs(false);
      }
    };

    fetchDocuments();
  }, [roleScope]);

  // Fetch FAQs filtered by role_scope
  useEffect(() => {
    const fetchFaqs = async () => {
      setIsLoadingFaqs(true);
      try {
        let query = supabase
          .from('faqs')
          .select('*')
          .order('created_at');

        if (roleScope) {
          query = query.eq('role_scope', roleScope);
        } else {
          query = query.is('role_scope', null);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching FAQs:', error);
          toast({
            title: 'Fout',
            description: 'Kon veelgestelde vragen niet laden.',
            variant: 'destructive',
          });
          return;
        }

        setFaqs(data ?? []);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setIsLoadingFaqs(false);
      }
    };

    fetchFaqs();
  }, [roleScope]);

  const handleOpenDocEditor = (doc: LegalDocument) => {
    setEditingDoc(doc);
    setEditorContent(doc.content);
  };

  const handleSaveDocument = async () => {
    if (!editingDoc) return;

    setIsSavingDoc(true);
    try {
      const { error } = await supabase
        .from('legal_documents')
        .update({ content: editorContent, updated_at: new Date().toISOString() })
        .eq('id', editingDoc.id);

      if (error) throw error;

      setDocuments(
        documents.map((doc) =>
          doc.id === editingDoc.id
            ? { ...doc, content: editorContent, updated_at: new Date().toISOString() }
            : doc,
        ),
      );
      setEditingDoc(null);

      toast({
        title: 'Succes',
        description: `${editingDoc.name} succesvol opgeslagen!`,
      });
    } catch (error) {
      console.error('Error saving document:', error);
      toast({
        title: 'Fout',
        description: 'Opslaan van document mislukt.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDoc(false);
    }
  };

  const handleCreateDocument = async () => {
    if (!newDocName.trim()) {
      toast({
        title: 'Fout',
        description: 'Vul een naam in voor het document.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingDoc(true);
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .insert({
          name: newDocName.trim(),
          content: '',
          role_scope: roleScope,
        })
        .select()
        .single();

      if (error) throw error;

      setDocuments([...documents, data]);
      setNewDocName('');

      toast({
        title: 'Succes',
        description: `Document "${data.name}" aangemaakt!`,
      });
    } catch (error) {
      console.error('Error creating document:', error);
      toast({
        title: 'Fout',
        description: 'Aanmaken van document mislukt.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingDoc(false);
    }
  };

  const handleDeleteDocument = async (id: string) => {
    const doc = documents.find((d) => d.id === id);
    if (!confirm(`Weet u zeker dat u "${doc?.name}" wilt verwijderen?`)) return;

    try {
      const { error } = await supabase.from('legal_documents').delete().eq('id', id);
      if (error) throw error;

      setDocuments(documents.filter((d) => d.id !== id));
      toast({
        title: 'Succes',
        description: 'Document succesvol verwijderd!',
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Fout',
        description: 'Verwijderen van document mislukt.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveContactEmail = async () => {
    if (!contactEmail) {
      toast({
        title: 'Fout',
        description: 'Vul een e-mailadres in.',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingEmail(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ key: emailKey, value: contactEmail, updated_at: new Date().toISOString() });

      if (error) throw error;

      toast({
        title: 'Succes',
        description: 'E-mailadres voor contact succesvol bijgewerkt!',
      });
    } catch (error) {
      console.error('Fout bij het bijwerken van e-mailadres voor contact:', error);
      toast({
        title: 'Fout',
        description: 'Bijwerken van e-mailadres voor contact mislukt.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleAddFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast({
        title: 'Fout',
        description: 'Vul alstublieft zowel het vraag- als antwoordveld in.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('faqs')
        .insert([{ question: newFaq.question, answer: newFaq.answer, role_scope: roleScope }])
        .select()
        .single();

      if (error) throw error;

      setFaqs([...faqs, data]);
      setNewFaq({ question: '', answer: '' });

      toast({
        title: 'Succes',
        description: 'FAQ succesvol toegevoegd!',
      });
    } catch (error) {
      console.error('Fout bij het toevoegen van FAQ:', error);
      toast({
        title: 'Fout',
        description: 'Toevoegen van FAQ mislukt.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateFaq = async (id: string) => {
    const faq = faqs.find((f) => f.id === id);
    if (!faq) return;

    try {
      const { error } = await supabase
        .from('faqs')
        .update({ question: faq.question, answer: faq.answer })
        .eq('id', id);

      if (error) throw error;

      setEditingFaq(null);
      toast({
        title: 'Succes',
        description: 'FAQ succesvol bijgewerkt!',
      });
    } catch (error) {
      console.error('Fout bij het bijwerken van FAQ:', error);
      toast({
        title: 'Fout',
        description: 'Bijwerken van FAQ mislukt.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteFaq = async (id: string) => {
    if (!confirm('Weet u zeker dat u deze FAQ wilt verwijderen?')) return;

    try {
      const { error } = await supabase.from('faqs').delete().eq('id', id);

      if (error) throw error;

      setFaqs(faqs.filter((faq) => faq.id !== id));
      toast({
        title: 'Succes',
        description: 'FAQ succesvol verwijderd!',
      });
    } catch (error) {
      console.error('Fout bij het verwijderen van FAQ:', error);
      toast({
        title: 'Fout',
        description: 'Verwijderen van FAQ mislukt.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Toaster />
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">{isPreviewMode ? 'HnH Instellingen' : 'Beheerdersinstellingen'}</h1>
      </div>

      <Tabs defaultValue="contact">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="contact">Contactgegevens</TabsTrigger>
          <TabsTrigger value="documents">Documenten</TabsTrigger>
          <TabsTrigger value="faqs">Veelgestelde Vragen</TabsTrigger>
        </TabsList>

        {/* Section 1: Contact Details */}
        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Contactinformatie</CardTitle>
              <CardDescription>
                Stel het contact e-mailadres in voor bugs en suggesties
                {isPreviewMode ? ' (HnH gebruikers)' : ''}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">
                  Contact E-mail voor Bugs & Suggesties
                </Label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <Input
                    id="contactEmail"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="support@example.com"
                  />
                </div>
              </div>
              <Button onClick={handleSaveContactEmail} disabled={isSavingEmail || isLoadingEmail}>
                {isSavingEmail ? 'Opslaan...' : 'Contactgegevens Opslaan'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Section 2: Documents */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Juridische Documenten{isPreviewMode ? ' (HnH)' : ''}</CardTitle>
              <CardDescription>
                Beheer de juridische documenten die aan {isPreviewMode ? 'HnH ' : ''}gebruikers worden
                getoond in de app.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {isLoadingDocs ? (
                  <div className="py-4 text-center text-muted-foreground">
                    Laden van documenten...
                  </div>
                ) : documents.length === 0 ? (
                  <div className="py-4 text-center text-muted-foreground">
                    Geen documenten gevonden.
                  </div>
                ) : (
                  documents.map((document) => (
                    <div
                      key={document.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <FileText className="h-6 w-6 text-blue-500" />
                        <div>
                          <h3 className="font-medium">{document.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            Laatst bijgewerkt:{' '}
                            {new Date(document.updated_at).toLocaleDateString('nl-NL')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDocEditor(document)}
                        >
                          Bewerken
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteDocument(document.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}

                {/* Add new document */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Input
                    value={newDocName}
                    onChange={(e) => setNewDocName(e.target.value)}
                    placeholder="Naam nieuw document..."
                    className="flex-grow"
                  />
                  <Button onClick={handleCreateDocument} disabled={isCreatingDoc || !newDocName.trim()}>
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Toevoegen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Document Editor Dialog */}
          <Dialog
            open={editingDoc !== null}
            onOpenChange={(open) => {
              if (!open) setEditingDoc(null);
            }}
          >
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>{editingDoc?.name}</DialogTitle>
                <DialogDescription>
                  Bewerk de inhoud van dit document. Wijzigingen worden pas
                  opgeslagen wanneer u op &quot;Opslaan&quot; klikt.
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto min-h-0">
                <RichTextEditor
                  content={editorContent}
                  onChange={setEditorContent}
                  placeholder="Begin met het schrijven van de documentinhoud..."
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEditingDoc(null)}
                >
                  Annuleren
                </Button>
                <Button onClick={handleSaveDocument} disabled={isSavingDoc}>
                  {isSavingDoc ? 'Opslaan...' : 'Opslaan'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Section 3: FAQs */}
        <TabsContent value="faqs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Veelgestelde Vragen{isPreviewMode ? ' (HnH)' : ''}</CardTitle>
              <CardDescription>
                Beheer de veelgestelde vragen die aan {isPreviewMode ? 'HnH ' : ''}gebruikers worden getoond
                in de app.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Existing FAQs */}
                {isLoadingFaqs ? (
                  <div className="py-4 text-center text-muted-foreground">
                    Laden van veelgestelde vragen...
                  </div>
                ) : faqs.length === 0 ? (
                  <div className="py-4 text-center text-muted-foreground">
                    Geen veelgestelde vragen gevonden.
                  </div>
                ) : null}
                {faqs.map((faq) => (
                  <div key={faq.id} className="p-4 border rounded-lg space-y-2">
                    {editingFaq === faq.id ? (
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor={`question-${faq.id}`}>Vraag</Label>
                          <Input
                            id={`question-${faq.id}`}
                            value={faq.question}
                            onChange={(e) => {
                              setFaqs(
                                faqs.map((f) =>
                                  f.id === faq.id
                                    ? { ...f, question: e.target.value }
                                    : f,
                                ),
                              );
                            }}
                          />
                        </div>
                        <div>
                          <Label htmlFor={`answer-${faq.id}`}>Antwoord</Label>
                          <Textarea
                            id={`answer-${faq.id}`}
                            value={faq.answer}
                            onChange={(e) => {
                              setFaqs(
                                faqs.map((f) =>
                                  f.id === faq.id
                                    ? { ...f, answer: e.target.value }
                                    : f,
                                ),
                              );
                            }}
                            rows={4}
                          />
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleUpdateFaq(faq.id)}
                            size="sm"
                          >
                            Opslaan
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setEditingFaq(null)}
                            size="sm"
                          >
                            Annuleren
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <h3 className="font-medium">{faq.question}</h3>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingFaq(faq.id)}
                            >
                              Bewerken
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteFaq(faq.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-muted-foreground">{faq.answer}</p>
                      </>
                    )}
                  </div>
                ))}

                {/* Add new FAQ */}
                <Card className="border-dashed">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      Nieuwe FAQ Toevoegen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="new-question">Vraag</Label>
                        <Input
                          id="new-question"
                          value={newFaq.question}
                          onChange={(e) =>
                            setNewFaq({ ...newFaq, question: e.target.value })
                          }
                          placeholder="Voer de vraag in"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-answer">Antwoord</Label>
                        <Textarea
                          id="new-answer"
                          value={newFaq.answer}
                          onChange={(e) =>
                            setNewFaq({ ...newFaq, answer: e.target.value })
                          }
                          placeholder="Voer het antwoord in"
                          rows={4}
                        />
                      </div>
                      <Button onClick={handleAddFaq} className="w-full">
                        <PlusCircle className="h-4 w-4 mr-2" />
                        FAQ Toevoegen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
